import { Component, OnDestroy, OnInit, signal } from '@angular/core';
import { Township, VoucherGroup } from '../interfaces';
import { ActivatedRoute } from '@angular/router';
import { lastValueFrom, Subscription } from 'rxjs';
import {
  FormBuilder,
  FormGroup,
  FormArray,
  AbstractControl,
  ValidationErrors,
  Validators,
  FormControl,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { db, functions } from '../app.component';
import { doc, getDoc } from 'firebase/firestore';
import { getTownshipImage, updateTheme } from '../globals';
import { httpsCallable } from 'firebase/functions';
import { PublicFunctionsService } from '../services/public-functions.service';
import { CustomValidators } from 'angular-custom-validators';
import { InfoDialogComponent } from '../dialogs/info-dialog/info-dialog.component';
import { ConfirmDialogComponent } from '../dialogs/confirm-dialog/confirm-dialog.component';
import { NgStyle } from '@angular/common';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatLabel, MatError, MatHint } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';

@Component({
  selector: 'app-collective-request',
  templateUrl: './collective-request.component.html',
  styleUrl: './collective-request.component.scss',
  imports: [
    NgStyle,
    MatButton,
    MatIcon,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatError,
    MatIconButton,
    MatHint,
    MatProgressSpinner,
  ],
})
export class CollectiveRequestComponent implements OnInit, OnDestroy {
  readonly db = db;
  readonly functions = functions;
  loaded = signal(false);
  campaignInactive = signal(false);
  townshipId = signal<string>(undefined);
  township = signal<Township>(undefined);
  voucherGroupId = signal<string>(undefined);
  voucherGroup = signal<VoucherGroup>(undefined);
  townshipImage = signal<string>(undefined);
  collectiveForm = new FormGroup({
    addresses: this.fb.array([]),
  });
  highContrast = signal(false);
  awaitingRequest = signal(false);
  vouchersUnavailable = signal(false);
  private readonly subscriptions = new Subscription();

  constructor(
    private route: ActivatedRoute,
    private fb: FormBuilder,
    public dialog: MatDialog,
    private snackBar: MatSnackBar,
    private publicFunctionsService: PublicFunctionsService,
  ) {}

  ngOnInit(): void {
    if (localStorage.getItem('highContrast') && localStorage.getItem('highContrast') === '1') {
      this.highContrast.set(true);
    }

    this.subscriptions.add(
      this.route.params.subscribe(async (params) => {
        console.log('params', params);
        if (!params.townshipId || !params.voucherGroupId) {
          console.error('missing parameters');
          return;
        }
        this.townshipId.set(params.townshipId);
        this.voucherGroupId.set(params.voucherGroupId);
        const townshipRef = doc(this.db, `township/${this.townshipId()}/settings/public`);
        const townshipSnap = await getDoc(townshipRef);
        this.township.set(townshipSnap.data() as Township);
        console.log('township', this.township());
        if (!this.township()) {
          return console.error('could not load township');
        }
        this.township.update((t) => {
          t.logoImageUrl = getTownshipImage(t);
          return t;
        });
        if (this.township()?.hideSource) {
          this.campaignInactive.set(true);
          return;
        }
        updateTheme({
          primary: this.township().primaryColor,
          secondary: this.township().accentColor,
        });
        const voucherGroupRef = doc(
          this.db,
          `township/${this.townshipId()}/voucherGroups/${this.voucherGroupId()}`,
        );
        const voucherGroupSnap = await getDoc(voucherGroupRef);
        this.voucherGroup.set(voucherGroupSnap.data() as VoucherGroup);
        if (!this.voucherGroup()) {
          return console.error('could not load voucherGroup');
        }
        console.log('voucherGroup', this.voucherGroup());
        if (this.voucherGroup()?.hidden) {
          this.campaignInactive.set(true);
          return;
        }
        if (this.voucherGroup()?.type !== 'collectiveBonus') {
          return console.error('incorrect voucherGroup type');
        }

        const availableVouchers = this.voucherGroup().statistics?.availableVouchers ?? 0;

        // Initialize form with minimum number of input fields
        this.initializeAddressFields();

        if (availableVouchers < (this.voucherGroup().minParticipants || 1)) {
          this.vouchersUnavailable.set(true);
          this.collectiveForm.disable();
        }

        this.loaded.set(true);
      }),
    );
  }

  initializeAddressFields() {
    const minParticipants = this.voucherGroup()?.minParticipants || 1;
    // Clear existing controls
    this.addresses.clear();
    // Add the minimum required number of controls
    for (let i = 0; i < minParticipants; i++) {
      this.addresses.push(this.createAddressGroup());
    }
  }

  createAddressGroup() {
    return new FormGroup(
      {
        postal: new FormControl('', [Validators.required, CustomValidators.postal]),
        houseNumber: new FormControl('', Validators.required),
        houseNumberAddition: new FormControl('', [Validators.pattern(/^[a-zA-Z0-9]*$/)]),
      },
      { validators: this.duplicateAddressValidator.bind(this) },
    );
  }

  duplicateAddressValidator(group: AbstractControl): ValidationErrors | null {
    const postal = group.get('postal')?.value?.trim();
    const houseNumber = group.get('houseNumber')?.value?.trim();
    const addition = group.get('houseNumberAddition')?.value?.trim() || '';

    if (!postal || !houseNumber) {
      return null;
    }

    const formArray = group.parent as FormArray;

    if (!formArray) {
      return null;
    }

    const hasDuplicate = formArray.controls.some(
      (c) =>
        c !== group &&
        c.get('postal')?.value?.trim() === postal &&
        c.get('houseNumber')?.value?.trim() === houseNumber &&
        (c.get('houseNumberAddition')?.value?.trim() || '') === addition,
    );

    return hasDuplicate ? { duplicate: true } : null;
  }

  setHighContrast() {
    this.highContrast.update((v) => !v);
    localStorage.setItem('highContrast', this.highContrast() ? '1' : '0');
  }

  get addresses() {
    return this.collectiveForm.get('addresses') as FormArray;
  }

  addAddress() {
    this.addresses.push(this.createAddressGroup());
    this.updateAllValidators();
  }

  removeAddress(index: number) {
    if (this.addresses.length > 1) {
      this.addresses.removeAt(index);
      this.updateAllValidators();
    }
  }

  updateAllValidators(changedIndex: number = -1) {
    // Update all controls to re-run validation
    this.addresses.controls.forEach((control, index) => {
      const serverError = control.errors?.['serverError'];
      control.updateValueAndValidity();
      if (index !== changedIndex && serverError) {
        const currentErrors = control.errors || {};
        control.setErrors({ ...currentErrors, serverError });
      }
    });
  }

  canSubmit(): boolean {
    const minParticipants = this.voucherGroup()?.minParticipants || 0;
    const filledAddresses = this.getFilledAddressesCount();
    return filledAddresses >= minParticipants && this.collectiveForm.valid;
  }

  getFilledAddressesCount(): number {
    return this.addresses.controls.filter(
      (control) => control.get('postal')?.value && control.get('houseNumber')?.value,
    ).length;
  }

  async submitCollectiveRequest() {
    if (!this.canSubmit() || this.awaitingRequest()) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Aanvraag indienen',
        message:
          'Weet u zeker dat u alle adressen hebt toegevoegd die mee willen doen met uw collectief?\n\r\n\rDit kan later niet meer gewijzigd worden.',
        confirmText: 'Ja, indienen',
        cancelText: 'Annuleren',
      },
    });

    const confirmed = await lastValueFrom(dialogRef.afterClosed());
    if (!confirmed) {
      return;
    }

    this.awaitingRequest.set(true);
    const addresses = this.addresses.controls
      .map((control) => ({
        postal: control.get('postal')?.value?.trim().toUpperCase(),
        houseNumber: control.get('houseNumber')?.value?.trim(),
        houseNumberAddition: control.get('houseNumberAddition')?.value?.trim().toLowerCase(),
      }))
      .filter((addr) => addr.postal && addr.houseNumber);

    const submitFunction = httpsCallable(this.functions, 'httpSubmitCollectiveRequest');

    try {
      const recaptchaToken = await this.publicFunctionsService.getRecaptchaToken(
        'submit_collective_request',
      );
      const data = {
        townshipId: this.townshipId(),
        voucherGroupId: this.voucherGroupId(),
        addresses: addresses,
        recaptchaToken,
      };
      const result = await submitFunction(data);
      console.log('Success', result);
      this.awaitingRequest.set(false);

      const dialogRef = this.dialog.open(InfoDialogComponent, {
        data: {
          title: 'Succes',
          message: 'De aanvraag is succesvol ingediend!',
          buttonText: 'Sluiten',
        },
      });
      this.subscriptions.add(
        dialogRef.afterClosed().subscribe(() => {
          window.location.reload();
        }),
      );
    } catch (err: any) {
      console.error('Error', err);
      this.awaitingRequest.set(false);

      if (err.message === 'voucher_group_hidden' || err.message === 'township_hidden') {
        this.snackBar.open('Deze campagne is momenteel niet actief.', 'X', {
          duration: 8000,
        });
        return;
      }

      let errorMessage = 'Er is een onbekende fout opgetreden.';
      const title = 'Fout';

      if (err.message === 'incorrect_vouchers' && err.details) {
        this.handleVoucherErrors(err.details);
        errorMessage =
          'Er zijn problemen met een of meer opgegeven adressen. Controleer de rood gemarkeerde velden.';
      } else if (err.message === 'incorrect_voucher_group') {
        errorMessage = 'Ongeldige bon groep.';
      } else if (err.message === 'no_vouchers_left') {
        errorMessage = 'Er zijn niet genoeg bonnen meer beschikbaar.';
      }

      this.dialog.open(InfoDialogComponent, {
        data: {
          title: title,
          message: errorMessage,
          isError: true,
          buttonText: 'Probeer opnieuw',
        },
      });
    }
  }

  handleVoucherErrors(details: any) {
    this.addresses.controls.forEach((control) => {
      const postal = control.get('postal')?.value?.trim();
      const houseNumber = control.get('houseNumber')?.value?.trim();
      const addition = control.get('houseNumberAddition')?.value?.trim();

      if (postal && houseNumber) {
        const key = `${postal}-${houseNumber}${addition ? `-${addition}` : ''}`;
        if (details[key]) {
          const errorType = details[key];
          console.log('errorType', errorType);
          let errorMessage = 'Er is een probleem met deze bon.';
          if (errorType === 'not_found' || errorType === 'invalid_status') {
            errorMessage = 'Geen geldige bon gevonden op dit adres.';
          } else if (errorType === 'invalid_group') {
            errorMessage = 'Dit adres kan niet aan het collectief toegevoegd worden.';
          } else if (errorType === 'already_in_collective') {
            errorMessage = 'Dit adres is al onderdeel van een ander collectief.';
          }
          control.setErrors({ serverError: errorMessage });
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
